import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function AboutPage() {
  const [loaded, setLoaded] = useState(false);
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    setLoaded(true);
  }, []);

  const values = [
    {
      icon: 'ri-scales-3-line',
      title: '邏輯嚴謹',
      description: '法律背景讓我習慣分析事證、釐清因果。面對資料集，我同樣以系統性方式拆解問題、驗證假設，不輕易下未經檢驗的結論。'
    },
    {
      icon: 'ri-search-eye-line',
      title: '細心仔細',
      description: '撰寫法律文書時，一字之差可能影響全局。這種對細節的高度敏感，讓我在資料清洗與品質驗證上格外謹慎，確保分析結果的可信度。'
    },
    {
      icon: 'ri-links-line',
      title: '跨領域視角',
      description: '具有法律實務以及反洗錢知識的背景，讓我能以不同維度理解金融與科技數據的意涵，在法律科技與金融科技領域提供獨特的分析洞察。'
    },
    {
      icon: 'ri-rocket-2-line',
      title: '主動學習',
      description: '轉職的動力來自對資料的真實興趣。我透過參加養成班快速學習 Python、SQL 與 Tableau等數據分析基礎知識，並透過個人專案將所學應用，持續精進自我不斷學習。'
    },
    {
      icon: 'ri-shield-check-line',
      title: '數據誠信',
      description: '法律職業對誠信的要求根植於我的工作態度。在資料分析中，我重視方法透明、結果可重現，不為求好看的數字而扭曲分析。'
    },
    {
      icon: 'ri-mind-map',
      title: '結構思考',
      description: '法律論證需要清晰的邏輯架構與有說服力的敘事。這讓我在呈現分析結果時，能夠將複雜數據轉化為決策者易於理解的清晰洞察。'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-7xl mx-auto text-center">
          <div
            className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <span className="text-sm font-semibold text-orange-700">關於 Frances</span>
          </div>
          <h1
            className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            認識 Frances Shen
          </h1>
          <p
            className={`text-lg text-gray-600 max-w-2xl mx-auto transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            法律實務出身，以嚴謹分析思維邁向數據領域的轉職者
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 px-4 md:px-8" ref={contentRef}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center">
            <div
              className={`relative transition-all duration-1000 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
            >
              <div className="relative rounded-3xl overflow-hidden group h-[600px]">
                <img
                  src="/photo.JPG"
                  alt="Frances Shen"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-orange-400 to-rose-400 rounded-full opacity-20 blur-2xl animate-pulse-soft"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full opacity-20 blur-2xl animate-pulse-soft" style={{ animationDelay: '2s' }}></div>
            </div>

            <div>
              <h2
                className={`text-2xl md:text-4xl font-bold text-gray-900 mb-6 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                關於我
              </h2>
              <p
                className={`text-base text-gray-600 leading-relaxed mb-4 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '100ms' }}
              >
                我是 Frances Shen，曾於律師事務所擔任行政秘書與法務專員，累積超過 2 年半的實務經驗，負責訴訟案件流程管理、法律文書草擬與審閱、法律研究及跨機關協調，養成了嚴謹的邏輯分析習慣與高度的細節敏感度。
              </p>
              <p
                className={`text-base text-gray-600 leading-relaxed mb-8 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '200ms' }}
              >
                目前正積極透過 530 小時的數據分析實務課程，系統性學習 Python、SQL 與 Tableau，並以個人專案累積實作經驗。我的目標是將法律領域的結構化思維與合規背景，結合數據技能，在金融科技或法律科技產業發揮跨域價值。
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-translate-2', text: '多益 TOEIC 795 分（2025.12）' },
                  { icon: 'ri-bank-line', text: '金融市場常識與職業道德　合格' },
                  { icon: 'ri-shield-check-line', text: '防制洗錢及打擊資恐人員職前研習班　合格' },
                  { icon: 'ri-scales-3-line', text: '律師事務所 2 年半工作經驗' },
                  { icon: 'ri-file-list-3-line', text: '協助處理超過 100 件法律案件' },
                  { icon: 'ri-bar-chart-2-line', text: '530 小時扎實數據分析實務經驗' },
                ].map((credential, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${400 + index * 100}ms` }}
                  >
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300">
                      <i className={`${credential.icon} text-orange-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{credential.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-gray-50 to-orange-50" ref={valuesRef}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2
              className={`text-2xl md:text-4xl font-bold text-gray-900 mb-6 transition-all duration-700 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              我的核心價值觀
            </h2>
            <p
              className={`text-base text-gray-600 max-w-2xl mx-auto transition-all duration-700 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '100ms' }}
            >
              法律訓練與轉職歷程，塑造了我面對工作與資料的方式
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-500 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${200 + index * 150}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-rose-100 rounded-2xl flex items-center justify-center mb-6 hover:scale-110 transition-transform duration-300">
                  <i className={`${value.icon} text-orange-600 text-3xl w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {value.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" ref={ctaRef}>
        <div
          className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <h2
            className={`text-2xl md:text-4xl font-bold text-gray-900 mb-6 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            對我的背景感興趣？
          </h2>
          <p
            className={`text-base text-gray-600 mb-10 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            歡迎查看我的個人專案與技能頁面，或直接與我聯絡——我正積極尋找數據分析師／數據工程師職位。
          </p>
          <div
            className={`flex items-center justify-center gap-4 flex-wrap transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <Link
              to="/side-projects"
              className="px-10 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              查看個人專案
            </Link>
            <Link
              to="/skills"
              className="px-10 py-4 rounded-full font-semibold text-base border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              我的技能
            </Link>
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
